"use client"

import { useActionState } from "react"
import Form from "next/form"
import { B2CSignUpActionState } from "@/actions/authActions"
import { zodResolver } from "@hookform/resolvers/zod"
import { LoaderCircle } from "lucide-react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { useForm } from "react-hook-form"

import { Button, Input, Label, RadioGroup, RadioGroupItem } from "@/components/ui/components"
import { UbB2CSignupFormStoryblok } from "@/components/storyblok/component-types"

import { B2CSignUpForm, B2CSignUpSchema } from "./signup"

const resolver = zodResolver(B2CSignUpSchema)

export const B2CSignUpFormClient = ({
  blok,
  action,
  values,
}: {
  blok: UbB2CSignupFormStoryblok
  action: (initialState: B2CSignUpActionState, formData: FormData) => Promise<B2CSignUpActionState>
  values: B2CSignUpForm
}) => {
  function formActionWithCaptcha(initialState: B2CSignUpActionState, formData: FormData) {
    return action(initialState, formData)
  }

  const [state, formAction, isPending] = useActionState(formActionWithCaptcha, {
    values,
    errors: {},
  })

  const { executeRecaptcha } = useGoogleReCaptcha()

  const { formState, register } = useForm({
    // Initialize the form with server-side errors
    errors: state.errors,
    // Make sure to use `onBlur`, `onChange`, or `onTouched` NOT `onSubmit`!
    mode: "onBlur",
    // Initialize the form with server-side values
    values: state.values,
    resolver,
  })

  return (
    <Form className={"flex flex-col gap-4"} action={formAction}>
      <div className={"grid grid-cols-1 gap-x-8 md:grid-cols-2"}>
        <div className={"flex flex-col gap-4"}>
          <h2 className={"text-xl font-bold"}>{blok.label_contact_data ?? "Kontaktangaben"}</h2>
          <div>
            <Label className={" font-bold"}>{blok.label_title}</Label>
            <RadioGroup className={"flex flex-row justify-between gap-2"} {...register("title")}>
              <div className="flex min-w-40 grow items-center space-x-2 rounded-lg border border-gray-300 bg-white px-5 py-3 text-base outline-none ring-0 hover:border-black">
                <RadioGroupItem value={blok.label_title_female ?? ""} id="r1" />
                <Label className={"font-bold"} htmlFor="r1">
                  {blok.label_title_female ?? ""}
                </Label>
              </div>
              <div className="flex min-w-40 grow items-center space-x-2 rounded-lg border border-gray-300 bg-white px-5 py-3 text-base outline-none ring-0 hover:border-black">
                <RadioGroupItem value={blok.label_title_male ?? ""} id="r2" />
                <Label className={"font-bold"} htmlFor="r2">
                  {blok.label_title_male ?? ""}
                </Label>
              </div>
              <div className="flex min-w-40 grow items-center space-x-2 rounded-lg border border-gray-300 bg-white px-5 py-3 text-base outline-none ring-0 hover:border-black">
                <RadioGroupItem value={blok.label_title_divers ?? ""} id="r3" />
                <Label className={"font-bold"} htmlFor="r3">
                  {blok.label_title_divers ?? ""}
                </Label>
              </div>
            </RadioGroup>

            {formState.errors.title ? <p role="alert">{formState.errors.title.message}</p> : null}
          </div>

          <div>
            <Label className={" font-bold"}>{blok.label_firstname}</Label>
            <Input {...register("firstName")} />

            {formState.errors.firstName ? <p role="alert">{formState.errors.firstName.message}</p> : null}
          </div>

          <div>
            <Label className={" font-bold"}>{blok.label_lastname}</Label>
            <Input {...register("lastName")} />
            {formState.errors.lastName ? <p role="alert">{formState.errors.lastName.message}</p> : null}
          </div>
        </div>

        <div className={"flex flex-col gap-4"}>
          <h2 className={"text-xl font-bold"}>{blok.label_login_data ?? "Login Informationen"}</h2>
          <div>
            <Label className={" font-bold"}>{blok.label_email}</Label>
            <Input {...register("email")} />
            {formState.errors.email ? <p role="alert">{formState.errors.email.message}</p> : null}
          </div>

          <div>
            <Label className={" font-bold"}>{blok.label_password}</Label>
            <Input {...register("password")} />
            {formState.errors.password ? <p role="alert">{formState.errors.password.message}</p> : null}
          </div>
        </div>
      </div>

      <div className={"flex flex-row justify-between gap-4"}>
        <Button type={"button"} variant={"outline_secondary"} size={"defaultNoGrow"} disabled={isPending}>
          {!isPending ? <p>{blok.label_cancel}</p> : <LoaderCircle className={"animate-spin"} />}
        </Button>

        <Button type={"submit"} size={"defaultNoGrow"} disabled={isPending}>
          {!isPending ? <p>{blok.label_submit}</p> : <LoaderCircle className={"animate-spin"} />}
        </Button>
      </div>
    </Form>
  )
}
